import { useEffect, useRef } from "react";
import moment from "moment";
import { isNil } from "lodash";
import { DATE_FORMAT } from "utils/constants";
import {
  isBooleanFieldChecked,
  toBooleanFieldValue,
} from "utils/helpers/booleanField";
import { applyTrailingSpaceStrip } from "utils/helpers/validations";
import { readItemField } from "utils/validation-rules";
import { applyHopBusy, markHopLoading } from "./hopBusy";

const INPUT_KIND_RADIO_GROUP = "radioGroup";
const INPUT_TYPE_FILE = "file";
const INPUT_TYPE_DATE = "date";
const INPUT_TYPE_CHECKBOX = "checkbox";
const TAG_INPUT = "INPUT";
const TAG_TEXTAREA = "TEXTAREA";
const TAG_SELECT = "SELECT";
const TAG_IMG = "IMG";
const ACTION_REMOVE_ITEM = "removeItem";
const HOP_COLLECTION_ACTIONS = new Set([
  "sendQuote",
  "sendQuoteAndSubscribe",
  "goToSubscription",
  "selectPlan",
]);
const SELECTED_TRUE = "true";
const SELECTED_FALSE = "false";
const HIDDEN_FLAG = "data-krs-hidden";
const HIDDEN_STYLE_ID = "krs-item-visibility";
const EMPTY_VALUE = "";
const ISO_DATE_PATTERN =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

const formatDisplayValue = (val) => {
  if (val instanceof Date) {
    const parsed = moment(val);
    return parsed.isValid() ? parsed.format(DATE_FORMAT) : String(val);
  }
  if (typeof val !== "string" || !ISO_DATE_PATTERN.test(val.trim())) {
    return String(val);
  }
  const parsed = moment(val);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : String(val);
};

const ITEM_LIST_ATTR = "data-item-list";
const ITEM_LIST_ITEM_ATTR = "data-item-list-item";
const ITEM_LIST_INDEX_ATTR = "data-item-list-index";

const toSelectedFlag = (isSelected) =>
  isSelected ? SELECTED_TRUE : SELECTED_FALSE;

// Fields of a nested list entry belong to that entry, not to the parent item.
const ownElements = (container, selector) =>
  Array.from(container.querySelectorAll(selector)).filter((el) => {
    const list =
      el.parentElement && el.parentElement.closest(`[${ITEM_LIST_ATTR}]`);
    return !list || !container.contains(list);
  });

const findFieldDef = (registryFields, fieldName) =>
  (registryFields || []).find((f) => f.key === fieldName) || null;

const replaceSelectOptions = (container, registryFields) => {
  const selects = container.querySelectorAll("select[data-field]");
  for (const el of selects) {
    const path = el.getAttribute("data-field");
    const fieldName = path.split(".").slice(1).join(".");
    const fieldDef = findFieldDef(registryFields, fieldName);
    if (!fieldDef || !Array.isArray(fieldDef.options)) continue;
    for (const o of Array.from(el.options)) {
      if (!o.disabled && o.value !== EMPTY_VALUE) o.remove();
    }
    for (const opt of fieldDef.options) {
      const optEl = el.ownerDocument.createElement("option");
      optEl.value = opt.value;
      optEl.textContent = opt.label;
      el.appendChild(optEl);
    }
  }
};

// `hidden` alone loses against the screen CSS (`.plan-card__cta` is a flex
// box), so the hidden state gets its own rule.
const ensureVisibilityStyle = (doc) => {
  if (!doc || !doc.getElementById || doc.getElementById(HIDDEN_STYLE_ID)) {
    return;
  }
  const style = doc.createElement("style");
  style.id = HIDDEN_STYLE_ID;
  style.textContent = `[${HIDDEN_FLAG}="true"] { display: none !important; }`;
  (doc.head || doc.body).appendChild(style);
};

const readVisibilityField = (item, path) => {
  const value = readItemField(item, path);
  if (!isNil(value) || !path.includes(".")) return value;
  return readItemField(item, path.split(".").slice(1).join("."));
};

/**
 * Per-item conditional: `data-visible-when="<itemKey>"` shows the element when
 * the item value matches `data-visible-value` (default "true"). Screen-level
 * conditional slots cannot reach here because `[data-kayros-slot]` is stripped
 * from item templates.
 */
const applyItemVisibility = (container, item) => {
  const conditionals = ownElements(container, "[data-visible-when]");
  if (!conditionals.length) return;
  ensureVisibilityStyle(container.ownerDocument);
  for (const el of conditionals) {
    const key = el.getAttribute("data-visible-when");
    const expected = el.getAttribute("data-visible-value") || SELECTED_TRUE;
    const value = readVisibilityField(item, key);
    const current = isNil(value) ? EMPTY_VALUE : String(value);
    const hidden = current !== expected;
    el.hidden = hidden;
    el.setAttribute(HIDDEN_FLAG, hidden ? SELECTED_TRUE : SELECTED_FALSE);
  }
};

const populateValues = (container, item) => {
  const fieldEls = ownElements(container, "[data-field]");
  for (const el of fieldEls) {
    const path = el.getAttribute("data-field");
    const fieldName = path.split(".").slice(1).join(".");
    const val = readItemField(item, fieldName);
    if (val === undefined || val === null) continue;
    const tag = el.tagName;
    if (tag === TAG_IMG) {
      if (String(val)) el.setAttribute("src", String(val));
      continue;
    }
    const radioButtons = el.querySelectorAll("[data-value]");
    if (
      el.getAttribute("data-input-type") === INPUT_KIND_RADIO_GROUP ||
      radioButtons.length
    ) {
      for (const sib of radioButtons) {
        sib.setAttribute(
          "data-selected",
          toSelectedFlag(sib.getAttribute("data-value") === String(val)),
        );
      }
      continue;
    }
    if (tag === TAG_INPUT || tag === TAG_TEXTAREA || tag === TAG_SELECT) {
      if (el.type === INPUT_TYPE_FILE) continue;
      if (el.type === INPUT_TYPE_DATE) {
        el.value = moment(val).format("YYYY-MM-DD");
      } else if (el.type === INPUT_TYPE_CHECKBOX) {
        el.checked = isBooleanFieldChecked(val);
      } else {
        el.value = String(val);
      }
    } else {
      el.textContent = formatDisplayValue(val);
      const pillWhen = el.getAttribute("data-pill-when");
      if (pillWhen) {
        const isPill = String(val).trim() === pillWhen;
        el.classList.toggle("ge-pill", isPill);
        el.classList.toggle("ge-amount", !isPill);
      }
    }
  }
};

/**
 * `data-item-list="<itemKey>"` repeats its `data-item-list-item` child once
 * per entry of that item array; the entry fields use the same data-field /
 * data-visible-when attributes as the parent item. An entry exposing
 * `selected` gets `data-selected`, and a `data-action` inside an entry is
 * reported with that entry.
 */
const captureItemLists = (container) =>
  ownElements(container, `[${ITEM_LIST_ATTR}]`).map((list) => {
    const entry = list.querySelector(`[${ITEM_LIST_ITEM_ATTR}]`);
    const template = entry ? entry.outerHTML : EMPTY_VALUE;
    list.innerHTML = EMPTY_VALUE;
    return { list, name: list.getAttribute(ITEM_LIST_ATTR), template };
  });

const renderItemLists = (itemLists, item) => {
  for (const { list, name, template } of itemLists || []) {
    list.innerHTML = EMPTY_VALUE;
    const entries = readItemField(item, name);
    if (!template || !Array.isArray(entries)) continue;
    entries.forEach((entry, index) => {
      const holder = list.ownerDocument.createElement("div");
      holder.innerHTML = template;
      const entryRoot = holder.firstElementChild;
      if (entryRoot) {
        entryRoot.setAttribute(ITEM_LIST_INDEX_ATTR, String(index));
        if (entry && !isNil(entry.selected)) {
          entryRoot.setAttribute(
            "data-selected",
            toSelectedFlag(String(entry.selected) === SELECTED_TRUE),
          );
        }
      }
      populateValues(holder, entry || {});
      applyItemVisibility(holder, entry || {});
      while (holder.firstChild) list.appendChild(holder.firstChild);
    });
  }
};

const itemListEntryOf = (element, item) => {
  const entryEl = element.closest(`[${ITEM_LIST_INDEX_ATTR}]`);
  const list = entryEl && entryEl.closest(`[${ITEM_LIST_ATTR}]`);
  if (!list) return null;
  const entries = readItemField(item, list.getAttribute(ITEM_LIST_ATTR));
  const index = Number(entryEl.getAttribute(ITEM_LIST_INDEX_ATTR));
  return Array.isArray(entries) ? entries[index] || null : null;
};

export const CollectionItem = ({
  template,
  item,
  onFieldChange,
  onRemove,
  onAction,
  registryFields,
  selected,
}) => {
  const containerRef = useRef(null);
  const itemListsRef = useRef([]);
  const itemRef = useRef(item);
  const onFieldChangeRef = useRef(onFieldChange);
  const onRemoveRef = useRef(onRemove);
  const onActionRef = useRef(onAction);
  const registryFieldsRef = useRef(registryFields);

  itemRef.current = item;
  onFieldChangeRef.current = onFieldChange;
  onRemoveRef.current = onRemove;
  onActionRef.current = onAction;
  registryFieldsRef.current = registryFields;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.innerHTML = template;
    container.removeAttribute("data-kayros-slot");
    const itemRoot = container.firstElementChild;
    if (itemRoot) {
      itemRoot.hidden = false;
      itemRoot.removeAttribute("hidden");
    }
    const innerSlots = container.querySelectorAll("[data-kayros-slot]");
    for (const ds of innerSlots) ds.removeAttribute("data-kayros-slot");

    itemListsRef.current = captureItemLists(container);
    replaceSelectOptions(container, registryFieldsRef.current);
    populateValues(container, itemRef.current || {});
    applyItemVisibility(container, itemRef.current || {});
    renderItemLists(itemListsRef.current, itemRef.current || {});

    const persistItemField = (target) => {
      const fieldName = target.dataset.field.split(".").slice(1).join(".");
      onFieldChangeRef.current(fieldName, target.value);
    };

    const handleInput = (e) => {
      const target = e.target;
      if (!target || !target.dataset || !target.dataset.field) return;
      if (target.type === INPUT_TYPE_CHECKBOX) return;
      e.stopPropagation();
      persistItemField(target);
    };

    const handleFocusOut = (e) => {
      const target = e.target;
      if (!target || !target.dataset || !target.dataset.field) return;
      if (!applyTrailingSpaceStrip(target)) return;
      persistItemField(target);
    };

    const handleChange = (e) => {
      const target = e.target;
      if (!target || !target.dataset || !target.dataset.field) return;
      if (target.type === INPUT_TYPE_CHECKBOX) {
        e.stopPropagation();
        const fieldName = target.dataset.field.split(".").slice(1).join(".");
        onFieldChangeRef.current(
          fieldName,
          toBooleanFieldValue(target.checked),
        );
        return;
      }
      if (target.tagName === TAG_SELECT) {
        e.stopPropagation();
        const fieldName = target.dataset.field.split(".").slice(1).join(".");
        onFieldChangeRef.current(fieldName, target.value);
      }
    };

    const handleClick = (e) => {
      const actionBtn = e.target.closest && e.target.closest("[data-action]");
      if (actionBtn) {
        const action = actionBtn.getAttribute("data-action");
        if (action === ACTION_REMOVE_ITEM) {
          e.preventDefault();
          e.stopPropagation();
          onRemoveRef.current && onRemoveRef.current();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (HOP_COLLECTION_ACTIONS.has(action)) {
          if (
            actionBtn.disabled ||
            actionBtn.getAttribute("data-krs-hop") ||
            actionBtn.getAttribute("data-krs-state") === "blocked" ||
            actionBtn.getAttribute("aria-disabled") === "true"
          ) {
            return;
          }
          markHopLoading(actionBtn);
          applyHopBusy(actionBtn.ownerDocument, [], true);
        }
        if (onActionRef.current) {
          const entry = itemListEntryOf(actionBtn, itemRef.current || {});
          onActionRef.current(action, itemRef.current, {
            stay: actionBtn.getAttribute("data-stay") === SELECTED_TRUE,
            ...(entry ? { entry } : {}),
          });
        }
        return;
      }
      const valueBtn = e.target.closest && e.target.closest("[data-value]");
      if (valueBtn) {
        const fieldEl = valueBtn.closest("[data-field]");
        if (!fieldEl) return;
        e.preventDefault();
        e.stopPropagation();
        const value = valueBtn.getAttribute("data-value");
        const fieldName = fieldEl
          .getAttribute("data-field")
          .split(".")
          .slice(1)
          .join(".");
        onFieldChangeRef.current(fieldName, value);
        const siblings = fieldEl.querySelectorAll("[data-value]");
        for (const sib of siblings) {
          sib.setAttribute(
            "data-selected",
            toSelectedFlag(sib.getAttribute("data-value") === value),
          );
        }
      }
    };

    container.addEventListener("input", handleInput);
    container.addEventListener("change", handleChange);
    container.addEventListener("click", handleClick);
    container.addEventListener("focusout", handleFocusOut);

    return () => {
      container.removeEventListener("input", handleInput);
      container.removeEventListener("change", handleChange);
      container.removeEventListener("click", handleClick);
      container.removeEventListener("focusout", handleFocusOut);
    };
  }, [template]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    replaceSelectOptions(container, registryFields);
    populateValues(container, itemRef.current || {});
    applyItemVisibility(container, itemRef.current || {});
  }, [registryFields]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    populateValues(container, item || {});
    applyItemVisibility(container, item || {});
    renderItemLists(itemListsRef.current, item || {});
  }, [item]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const itemEl = container.firstElementChild;
    if (!itemEl) return;
    itemEl.setAttribute("data-selected", toSelectedFlag(selected));
  }, [selected, template]);

  return <div ref={containerRef} />;
};

export default CollectionItem;
